import type {
    BulkDeleteResult,
    BulkSelectionPayload,
} from '@/services/engine/types';
import type { MouseEvent, ReactNode } from 'react';
import type { DirectoryStats } from '@/types/common';
import type { PaginatedResponse } from '@/types/responses';

export type { DirectoryStats };

export enum StatusFilter {
    ALL = 'all',
    UNKNOWN = 'unknown',
    RECOGNIZED = 'recognized',
}

export interface DirectorySortOption {
    value: string;
    labelKey: string;
}

export interface DirectoryGridFetchParams {
    page: number;
    size: number;
    search?: string;
    known?: boolean;
    tags?: string[];
    vehicleTypes?: string[];
    orderBy?: string;
}

export interface DirectoryGridConfig<
    TItem,
    TPayload = Record<string, unknown>,
> {
    title: string;
    parentTitle?: string;
    pageKey: string;
    cacheKeyPrefix: string;
    statsCacheKey: string;
    fetchItems: (
        params: DirectoryGridFetchParams,
    ) => Promise<PaginatedResponse<TItem>>;
    fetchStats: () => Promise<DirectoryStats>;
    fetchTags?: () => Promise<string[]>;
    tagsCacheKey?: string;
    vehicleTypeOptions: string[];
    sortOptions: DirectorySortOption[];
    defaultSort: string;
    orderByMap: Record<string, string>;
    itemRoute: string;
    routeParamKey: string;
    searchPlaceholder: string;
    tabLabels: { all: string; unknown: string; recognized: string };
    messages: {
        empty: string;
        loadingMore: string;
        allLoaded: string;
        failedToLoad: string;
    };
    selectionEntityLabel: string;
    bulkDeleteItems?: (
        selection: BulkSelectionPayload,
        filters?: Record<string, unknown>,
    ) => Promise<BulkDeleteResult>;
    canDeleteItem?: (item: TItem) => boolean;
    updateItem: (id: string, payload: TPayload) => Promise<unknown>;
    markAsUnknownUpdate?: () => MarkAsUnknownUpdate<TItem, TPayload>;
    onUpdateSuccess?: (mode: ModalMode) => void;
    onUpdateError?: (
        mode: ModalMode,
        context: UpdateErrorContext<TPayload>,
    ) => void;
    onMarkAsUnknownSuccess?: () => void;
    onMarkAsUnknownError?: () => void;
    onDeleteSuccess?: (deleted: number) => void;
    onDeleteError?: (error: unknown) => void;
    onPartialDelete?: (result: BulkDeleteResult) => void;
    onDeleteInProgress?: () => void;
    onMergeSingleItem?: (item: TItem) => void;
}

export interface UpdateErrorContext<TPayload> {
    itemId: string;
    payload: TPayload;
    error: unknown;
}

export interface MarkAsUnknownUpdate<TItem, TPayload> {
    payload: TPayload;
    optimisticPatch: Partial<TItem>;
}

export type ModalMode = 'recognize' | 'edit';

export interface EditModalRenderProps<
    TItem,
    TPayload = Record<string, unknown>,
> {
    isOpen: boolean;
    onClose: () => void;
    isSubmitting: boolean;
    mode: ModalMode;
    item: TItem | undefined;
    onUpdate: (itemId: string, payload: TPayload) => Promise<boolean>;
}

export interface DeleteConfirmRenderProps<TItem> {
    isOpen: boolean;
    item: TItem | undefined;
    count: number;
    isDeleting: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export interface DirectoryCardHandlers {
    onClick: (id: string, event?: MouseEvent) => void;
    onEdit: (id: string) => void;
    onMarkAsRecognized: (id: string) => void;
    onMarkAsUnknown: (id: string) => void;
    onDelete?: (id: string) => void;
    onMerge?: () => void;
    checked?: boolean;
    isDeleting?: boolean;
    selectionDisabled?: boolean;
}
